import React, { useState, useEffect, lazy, Suspense, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import ListingCard from "../components/ui/ListingCard";
import {
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  TagIcon,
  StarIcon,
  ArrowLeftIcon,
  CheckBadgeIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";

import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";

const ImageCarousel = lazy(() => import("../components/ui/ImageCarousel"));
const GoogleMap = lazy(() => import("../components/ui/GoogleMap"));

const CompanyPublicPage = () => {
  const { companyId } = useParams();
  const { user } = useAuth();

  const [company, setCompany] = useState(null);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("about");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [favorites, setFavorites] = useState(new Set());

  const hasServices = services.length > 0;
  const hasProducts = products.length > 0;

  const categories = useMemo(() => {
    return [
      "all",
      ...new Set(services.map((service) => service.category).filter(Boolean)),
    ];
  }, [services]);

  const filteredServices = useMemo(() => {
    if (selectedCategory === "all") {
      return services;
    }

    return services.filter((service) => service.category === selectedCategory);
  }, [services, selectedCategory]);

  const navigationTabs = useMemo(() => {
    const tabs = [{ id: "about", label: "О компании" }];

    if (hasServices) {
      tabs.push({ id: "services", label: "Услуги" });
    }

    if (hasProducts) {
      tabs.push({ id: "products", label: "Товары" });
    }

    tabs.push({
      id: "reviews",
      label: "Отзывы",
      count: company?.review_count || 0,
    });

    return tabs;
  }, [hasServices, hasProducts, company?.review_count]);

  useEffect(() => {
    if (companyId) {
      fetchCompanyData();
    }
  }, [companyId]);

  useEffect(() => {
    const isCurrentTabHidden =
      (activeTab === "services" && !hasServices) ||
      (activeTab === "products" && !hasProducts);

    if (isCurrentTabHidden) {
      setActiveTab("about");
    }
  }, [activeTab, hasServices, hasProducts]);

  useEffect(() => {
    if (!categories.includes(selectedCategory)) {
      setSelectedCategory("all");
    }
  }, [categories, selectedCategory]);

  const fetchCompanyData = async () => {
    setLoading(true);

    try {
      const companyData = await api.get(`/public/companies/${companyId}`);

      if (companyData.success) {
        setCompany(companyData.company);
      }

      const servicesData = await api.get(
        `/public/companies/${companyId}/services`,
      );

      if (servicesData.success) {
        setServices(
          Array.isArray(servicesData.services) ? servicesData.services : [],
        );
      } else {
        setServices([]);
      }

      const productsData = await api.get(
        `/public/companies/${companyId}/products`,
      );

      if (productsData.success) {
        setProducts(
          Array.isArray(productsData.products) ? productsData.products : [],
        );
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error("Error fetching company data:", error);
      setServices([]);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = (id) => {
    setFavorites((prev) => {
      const newFavs = new Set(prev);

      if (newFavs.has(id)) {
        newFavs.delete(id);
      } else {
        newFavs.add(id);
      }

      return newFavs;
    });
  };

  const handleAddToCart = (item) => {
    console.log("Добавлено в корзину:", item);
    // Здесь потом можно подключить Context, Zustand, Redux или другой стор корзины
  };

  const handleOpenCompanyChat = () => {
    if (!company) return;

    window.dispatchEvent(
      new CustomEvent("open-zootel-chat", {
        detail: {
          companyId: company.id,
          companyName: company.name,
        },
      }),
    );
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < 5; i += 1) {
      if (i < fullStars) {
        stars.push(
          <StarSolidIcon key={i} className="w-5 h-5 text-orange-500" />,
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <div key={i} className="relative">
            <StarIcon className="w-5 h-5 text-zinc-300" />
            <div className="absolute inset-0 overflow-hidden w-1/2">
              <StarSolidIcon className="w-5 h-5 text-orange-500" />
            </div>
          </div>,
        );
      } else {
        stars.push(<StarIcon key={i} className="w-5 h-5 text-zinc-300" />);
      }
    }

    return stars;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 border-4 border-zinc-200 border-t-zinc-900 rounded-full animate-spin mb-4" />
          <span className="text-sm font-black text-zinc-400 uppercase tracking-widest">
            Загрузка
          </span>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center bg-white p-12 rounded-[40px] shadow-sm border border-zinc-100">
          <h1 className="text-3xl font-black text-zinc-900 mb-4">
            Компания не найдена
          </h1>

          <Link
            to="/marketplace"
            className="inline-flex items-center text-orange-600 font-bold hover:gap-2 transition-all"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Вернуться в маркетплейс
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 pb-20">
      {/* HEADER HERO */}
      <div className="relative h-[500px] bg-zinc-900 overflow-hidden">
        {company.media_gallery?.length > 0 ? (
          <div className="relative w-full h-full">
            <Suspense
              fallback={<div className="h-full bg-zinc-800 animate-pulse" />}
            >
              <ImageCarousel
                images={company.media_gallery}
                alt={company.name}
                className="w-full h-full object-cover opacity-60"
              />
            </Suspense>

            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/20 to-transparent" />
          </div>
        ) : (
          <div className="w-full h-full bg-zinc-900" />
        )}

        {/* Hero Overlay Info */}
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center md:items-end gap-8 text-center md:text-left">
              {company.logo_url && (
                <img
                  src={company.logo_url}
                  alt={`${company.name} logo`}
                  className="w-32 h-32 rounded-[32px] border-4 border-white/10 shadow-2xl object-cover bg-white/5 backdrop-blur-sm"
                />
              )}

              <div className="flex-1 pb-2">
                <div className="flex items-center justify-center md:justify-start gap-3 mb-3">
                  <h1 className="text-5xl md:text-6xl font-black text-white tracking-tighter">
                    {company.name}
                  </h1>

                  {company.is_verified && (
                    <CheckBadgeIcon className="w-8 h-8 text-blue-400" />
                  )}
                </div>

                <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 text-zinc-300 font-bold">
                  <div className="flex items-center gap-2">
                    <MapPinIcon className="w-5 h-5 text-orange-500" />
                    <span>
                      {company.city}, {company.country}
                    </span>
                  </div>

                  {company.avg_rating > 0 && (
                    <div className="flex items-center gap-3 bg-white/10 px-4 py-2 rounded-2xl backdrop-blur-md">
                      <div className="flex">
                        {renderStars(company.avg_rating)}
                      </div>
                      <span className="text-white">
                        ({company.review_count} отзывов)
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="pb-4">
                {user?.role === "pet_owner" && (
                  <button
                    type="button"
                    onClick={handleOpenCompanyChat}
                    className="inline-flex items-center gap-2 rounded-2xl bg-white/90 px-5 py-3 text-[11px] font-black uppercase tracking-widest text-zinc-900 hover:bg-white transition-all"
                  >
                    <ChatBubbleLeftRightIcon className="h-4 w-4" />
                    Написать в чат
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TABS NAVIGATION */}
      <div className="sticky top-0 z-40 border-b border-zinc-100 bg-white/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto flex p-2 gap-2 overflow-x-auto no-scrollbar">
          {navigationTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-6 rounded-2xl font-bold text-sm transition-all flex items-center gap-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? "bg-zinc-900 text-white"
                  : "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
              }`}
            >
              {tab.label}

              {tab.id === "reviews" && tab.count > 0 && (
                <span
                  className={`py-0.5 px-2 rounded-lg text-[10px] font-black ${
                    activeTab === tab.id
                      ? "bg-white/20 text-white"
                      : "bg-zinc-100 text-zinc-500"
                  }`}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* MAIN CONTENT AREA */}
      <div className="max-w-6xl mx-auto py-12 px-4 md:px-6">
        {activeTab === "about" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Left Column: Description */}
            <div className="lg:col-span-2 space-y-8">
              <div className="bg-white rounded-[32px] border border-zinc-100 p-10 shadow-sm">
                <h2 className="text-3xl font-black text-zinc-900 mb-6">
                  Информация
                </h2>

                <div className="text-zinc-500 font-medium leading-relaxed text-lg space-y-4">
                  {company.description ? (
                    company.description
                      .split("\n")
                      .map((para, index) => <p key={index}>{para}</p>)
                  ) : (
                    <p>Описание компании не заполнено.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column: Sidebar */}
            <div className="space-y-6">
              <div className="bg-white rounded-[32px] border border-zinc-100 p-8 shadow-sm">
                <h3 className="text-xl font-black text-zinc-900 mb-6">
                  Контакты
                </h3>

                <div className="space-y-4">
                  {company.phone && (
                    <a
                      href={`tel:${company.phone}`}
                      className="group flex items-center p-4 rounded-2xl bg-zinc-50 hover:bg-zinc-100 transition-all border border-transparent hover:border-zinc-200"
                    >
                      <div className="bg-white p-2.5 rounded-xl shadow-sm mr-4">
                        <PhoneIcon className="w-5 h-5 text-zinc-900" />
                      </div>

                      <div className="flex flex-col">
                        <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">
                          Телефон
                        </span>
                        <span className="text-sm font-bold text-zinc-900">
                          {company.phone}
                        </span>
                      </div>
                    </a>
                  )}

                  {company.email && (
                    <a
                      href={`mailto:${company.email}`}
                      className="group flex items-center p-4 rounded-2xl bg-zinc-50 hover:bg-zinc-100 transition-all border border-transparent hover:border-zinc-200"
                    >
                      <div className="bg-white p-2.5 rounded-xl shadow-sm mr-4">
                        <EnvelopeIcon className="w-5 h-5 text-zinc-900" />
                      </div>

                      <div className="flex flex-col">
                        <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">
                          Email
                        </span>
                        <span className="text-sm font-bold text-zinc-900">
                          {company.email}
                        </span>
                      </div>
                    </a>
                  )}
                </div>
              </div>

              {/* MAP BLOCK */}
              <div className="bg-white rounded-[32px] border border-zinc-100 overflow-hidden shadow-sm">
                <div className="p-8 pb-4">
                  <h3 className="text-xl font-black text-zinc-900">Адрес</h3>
                  <p className="text-zinc-500 font-medium mt-1 text-sm">
                    {company.address || "Адрес не указан"}
                  </p>
                </div>

                <div className="h-[300px] m-4 mt-0 rounded-[24px] overflow-hidden grayscale-[0.5] hover:grayscale-0 transition-all duration-700 shadow-inner bg-zinc-100">
                  <Suspense
                    fallback={
                      <div className="h-full w-full animate-pulse bg-zinc-200" />
                    }
                  >
                    <GoogleMap
                      address={company.address}
                      coordinates={
                        company.latitude && company.longitude
                          ? {
                              lat: parseFloat(company.latitude),
                              lng: parseFloat(company.longitude),
                            }
                          : null
                      }
                      height="100%"
                      markerTitle={company.name}
                    />
                  </Suspense>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "services" && hasServices && (
          <div className="space-y-8">
            {/* Фильтр категорий */}
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-4">
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setSelectedCategory(category)}
                  className={`px-8 py-3 rounded-2xl font-black text-[10px] uppercase tracking-[0.15em] transition-all border whitespace-nowrap ${
                    selectedCategory === category
                      ? "bg-zinc-900 text-white border-zinc-900 shadow-xl shadow-zinc-100/20"
                      : "bg-white text-zinc-400 border-zinc-100 hover:border-zinc-300"
                  }`}
                >
                  {category === "all" ? "Все услуги" : category}
                </button>
              ))}
            </div>

            {/* Сетка услуг */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredServices.length > 0 ? (
                filteredServices.map((service) => (
                  <ListingCard
                    key={service.id}
                    item={{
                      ...service,
                      type: "service",
                      company: company.name,
                    }}
                    isFavorite={favorites.has(service.id)}
                    onToggleFavorite={toggleFavorite}
                    onAddToCart={handleAddToCart}
                  />
                ))
              ) : (
                <div className="col-span-full py-20 text-center bg-white rounded-[40px] border-2 border-dashed border-zinc-100">
                  <TagIcon className="w-12 h-12 text-zinc-200 mx-auto mb-4" />
                  <h3 className="text-xl font-black text-zinc-900">
                    Ничего не нашли
                  </h3>
                  <p className="text-zinc-500 font-medium">
                    Попробуйте выбрать другую категорию
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "products" && hasProducts && (
          <div className="space-y-8">
            {/* Сетка товаров */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {products.map((product) => (
                <ListingCard
                  key={product.id}
                  item={{
                    ...product,
                    type: "product",
                    company: company.name,
                  }}
                  isFavorite={favorites.has(product.id)}
                  onToggleFavorite={toggleFavorite}
                  onAddToCart={handleAddToCart}
                />
              ))}
            </div>
          </div>
        )}

        {activeTab === "reviews" && (
          <div className="bg-white rounded-[40px] border border-zinc-100 p-10 shadow-sm text-center">
            <h2 className="text-3xl font-black text-zinc-900 mb-3">Отзывы</h2>
            <p className="text-zinc-500 font-medium">
              Раздел отзывов будет доступен здесь.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(CompanyPublicPage);
